import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import $ from 'jquery';
import moment from 'moment';

import type { Service } from 'webapp-types';
import { BillingCycle, BillingFeature } from 'webapp/enums/enums';
import { getFormattedPrice, getIntervalTime } from 'webapp/utils/number';

import joinClasses from 'webapp/helpers/join-classes';
import didInsert from 'webapp/modifiers/did-insert';
import BarLoaderComponent from 'webapp/components/bar-loader';
import BillingPlanTopFeaturesComponent from 'webapp/components/billing/plan-top-features';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import ToolTipComponent from 'webapp/components/tool-tip';
import ToolTipWithIconComponent from 'webapp/components/tool-tip-with-icon';

import type BillingPlan from 'webapp/models-custom/billing-plan';
import type Promotion from 'webapp/models-custom/promotion';

interface Args {
	model: BillingPlan;
	isCurrentPlan: boolean;
	forBillingCycle: BillingCycle;
	previousModel?: BillingPlan;
}

interface FeatureInfo {
	name: string;
	info?: string;
	isEnabled?: boolean;
}

interface SliderUIParams {
	handle: HTMLElement;
	handleIndex: number;
	value: number;
}

export default class BillingPlanComponent extends Component<Args> {
	@service('billing') declare billingService: Service.Billing;
	@service('account') declare accountService: Service.Account;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('flow/promotions') declare flowPromotionsService: Service.Flow.Promotions;
	@service('user-settings') declare userSettingsService: Service.UserSettings;
	@service('popup') declare popupService: Service.Popup;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('automated-popup-queue')
	declare automatedPopupQueueService: Service.AutomatedPopupQueue;

	@tracked sourcesSliderCount;
	@tracked isEndAutomatedTrialInProgress = false;
	@tracked isHoveringOnSlider = false;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		let sourcesCount;

		if (this.keepDiscountAndCustomizations) {
			// this.billing.sourcesCurrent is the full number of connected DS.
			sourcesCount = Math.max(
				this.sourceSliderMinimumCount,
				this.billingService.sourcesCurrent,
			);
		} else {
			sourcesCount = this.currentSourceCount;
		}

		this.sourcesSliderCount = sourcesCount ? +sourcesCount : 1; // show minimal 1 on slider at initial load
	}

	get currentSourceCount() {
		const billingFeature = BillingFeature.SOURCES;
		if (this.args.model.isFree || this.args.model.isAnalyst) {
			return this.args.model.getFeatureValue(billingFeature) as number;
		}

		return Math.max(
			this.billingService.getFeatureCurrent(billingFeature),
			this.sourceSliderMinimumCount,
		);
	}

	get planTypeClass() {
		return 'plan--' + this.args.model.planType;
	}

	get is2026SeptBillingGroup() {
		return this.billingService.is2026SeptBillingGroup;
	}

	get activeClass() {
		return this.args.isCurrentPlan && !this.hasAnyPlanActiveAutomatedTrial ? 'active' : '';
	}

	get keepDiscountAndCustomizations() {
		return this.billingService.keepDiscountAndCustomizationsOnPlan(this.args.model);
	}

	get isRequestTrialInProgress() {
		return this.billingService.requestTrialInProgress;
	}

	get minimumBillableSources() {
		return this.billingService.sourcesMinimumBillable;
	}

	get freeSources() {
		return this.args.model.freeSources + this.billingService.sourcesGivenInPA;
	}

	get minimumSourcesCount() {
		/* If the number of DSC doesn't exceed this number, the total cost doesn't change */
		return this.args.model.freeSources;
	}

	get sourceSliderMinimumCount() {
		const defaultMinSourcesCount = this.minimumSourcesCount;

		/* Don't lower price under minimum billable sources, or plan minimum */
		return this.keepDiscountAndCustomizations
			? Math.max(defaultMinSourcesCount, this.minimumBillableSources)
			: defaultMinSourcesCount;
	}

	@cached
	get sourcesCount() {
		if (!this.isExtraSourcesAllowedOnPlan) {
			return 0;
		}

		if (this.isHoveringOnSlider) {
			return Math.max(this.sourcesSliderCount, this.sourceSliderMinimumCount);
		}

		return this.currentSourceCount;
	}

	get billedSourcesSliderCount() {
		// Dependent on slider value, but remove the free count
		const freeSources = this.keepDiscountAndCustomizations
			? this.freeSources
			: this.args.model.freeSources;

		const sourcesPayingFor = Math.max(
			0,
			this.sourcesCount - freeSources,
			this.minimumBillableSources - freeSources,
		);

		return sourcesPayingFor;
	}

	get isExtraSourcesAllowedOnPlan() {
		const plan = this.args.model;
		return plan.isExtraSourcesAllowedOnPlan;
	}

	get reserveSpaceForMiniInvoice() {
		const plan = this.args.model;
		return (
			this.args.isCurrentPlan &&
			!this.is2026SeptBillingGroup &&
			!plan.isFree &&
			!plan.isAnalyst &&
			!plan.isCustom
		);
	}

	get underHeaderFixedHeight() {
		return this.isExtraSourcesAllowedOnPlan || this.reserveSpaceForMiniInvoice;
	}

	get isSliderHidden() {
		return (
			!this.isExtraSourcesAllowedOnPlan ||
			(this.args.isCurrentPlan && !this.isHoveringOnSlider)
		);
	}

	get showPlanDescription() {
		return this.is2026SeptBillingGroup || this.args.model.isCustom;
	}

	get showCurrentPlanInfo() {
		const plan = this.args.model;
		return (
			this.args.isCurrentPlan &&
			!plan.isCustom &&
			!plan.isFree &&
			!this.is2026SeptBillingGroup
		);
	}

	get appliedDiscount() {
		let discount = 0;
		if (this.keepDiscountAndCustomizations) {
			discount = this.args.model.getDiscount(this.billingService.monthlyDiscount);
		}
		return -1 * discount;
	}

	get appliedDiscountFormatted() {
		return getFormattedPrice(this.appliedDiscount, 2);
	}

	get planSourcesSumPriceFormatted() {
		return this.args.model.getPriceWithSourcesFormatted(this.billedSourcesSliderCount);
	}

	get planSumPrice() {
		const sourcesPayingFor = this.billedSourcesSliderCount,
			monthlyDiscount = this.billingService.monthlyDiscount;

		let price;
		if (this.keepDiscountAndCustomizations) {
			price = this.args.model.getTotalPriceWithSourcesAndDiscount(
				sourcesPayingFor,
				monthlyDiscount,
			);
		} else {
			price = this.args.model.getTotalPriceWithSources(sourcesPayingFor);
		}

		return price;
	}

	get planSumPriceWithDiscountPromotion() {
		const selectedDiscountPromotion = this.selectedDiscountPromotion,
			planSumPrice = this.planSumPrice;

		if (selectedDiscountPromotion) {
			const discountAmmount = selectedDiscountPromotion.discountAmmount,
				discountMonths = Math.min(
					this.args.model.monthsInCycle,
					selectedDiscountPromotion.discountDurationInMonths,
				);

			return planSumPrice - discountAmmount * discountMonths;
		}

		return planSumPrice;
	}

	get planSumPriceWithDiscountPromotionFormatted() {
		return getFormattedPrice(this.planSumPriceWithDiscountPromotion, 0);
	}

	get planSumPriceMonthly() {
		const planPriceMonthly = this.planSumPrice / this.args.model.monthsInCycle;

		return Math.round(planPriceMonthly * 100) / 100;
	}

	get planSumPriceFormatted() {
		return getFormattedPrice(this.planSumPrice, 0);
	}

	get planSumPriceMonthlyFormatted() {
		return getFormattedPrice(this.planSumPriceMonthly, 0);
	}

	get planSumPriceMonthlyWithDiscountPromotion() {
		return this.planSumPriceMonthly - (this.selectedDiscountPromotion?.discountAmmount || 0);
	}

	get planSumPriceMonthlyWithDiscountPromotionFormatted() {
		return getFormattedPrice(this.planSumPriceMonthlyWithDiscountPromotion, 0);
	}

	get planSumPriceMonthlyNoCurrencyFormatted() {
		return getFormattedPrice(this.planSumPriceMonthly, 0, true);
	}

	get showBilledCycleTotal() {
		return !this.args.model.isMonthly && !this.args.model.isFree;
	}

	get canAnyPlanRequestTrial() {
		return (
			this.billingService.canRequestTrial ||
			this.flowPromotionsService.promotions.hasAnyHigherPlanAutomatedTrialOptionPromotions
		);
	}

	get hasAnyPlanActiveAutomatedTrial() {
		return this.flowPromotionsService.hasAnyPlanActiveAutomatedTrial;
	}

	get hasExtraAction() {
		return this.canAnyPlanRequestTrial || this.hasAnyPlanActiveAutomatedTrial;
	}

	get canRequestTrial() {
		return this.billingService.canRequestTrial || !!this.availableTrialPromotion;
	}

	get showTrialOption() {
		return this.canRequestTrial && !this.args.model.isFree;
	}

	get isDowngrade() {
		return this.billingService.isPlanDowngrade(this.args.model);
	}

	get isFreeOrStarter() {
		return this.args.model.isFree || this.args.model.isStarter;
	}

	get isPerformerOrGrowth() {
		return this.args.model.isPerformer || this.args.model.isGrowth;
	}

	get isLegacy() {
		return this.args.model.isLegacy;
	}

	get isSamePlanDifferentBillingCycle() {
		return this.billingService.activePlan?.planType === this.args.model.planType;
	}

	get isPlanPromoShown() {
		if (this.currentUserService.isAgencyUserInPaidClientSpace) {
			return false;
		}

		const { activePlan, isActiveTrial } = this.billingService;

		const model = this.args.model;

		const timestamp = this.userSettingsService.bestValuePlanBannerCooldownTimestamp;
		if (timestamp && moment().diff(moment.unix(timestamp), 'months') < 1) {
			return false;
		}

		if (isActiveTrial) {
			return model.isGrowth && !activePlan?.isPremium;
		} else {
			return (
				(model.isStarter && activePlan?.isFree) ||
				(model.isProfessional && activePlan?.isStarter) ||
				(model.isGrowth && activePlan?.isProfessional)
			);
		}
	}

	get activeTimeLeftOnTrialFormattedText() {
		const timeLeft = this.billingService.activeTimeLeft;
		let formattedText = 'Trialing';

		if (timeLeft > 44) {
			// left out few seconds text - too long for button
			formattedText += ` (${getIntervalTime(timeLeft, 'second', true)} left)`;
		}

		return formattedText;
	}

	private getFeatureInfoToShow(featureName: BillingFeature) {
		// returns feature info with values from either the model,
		// or (if this model is current plan's upgrade), the manually given
		// values that are higher than this models' defaults
		const featureOnModelPlan = this.args.model.features.getFeatureInfo(featureName);
		let featureInfo = featureOnModelPlan;
		if (this.keepDiscountAndCustomizations) {
			const featureOnActivePlan = this.billingService.getFeatureInfo(featureName),
				useFromActivePlan = this.billingService.compareFeatureValues(
					featureName,
					featureOnModelPlan?.value as number | null,
					featureOnActivePlan?.value as number | null,
				);
			if (useFromActivePlan) {
				featureInfo = featureOnActivePlan;
			}
		}
		return featureInfo;
	}

	get isUltimateAgencyPlan() {
		// one plan card
		return this.billingService.agencyPricing && this.billingService.is2026SeptBillingGroup;
	}

	get additionalFeaturesInfo(): { title: string; features: FeatureInfo[] } {
		const plan = this.args.model;

		let title = 'Includes:';
		let features: string[] = [];

		if (plan.isCustom) {
			if (this.is2026SeptBillingGroup) {
				title = 'Everything in Team, plus:';
			}
			features = this.billingService.customPlanFeatureItems;
		} else if (this.is2026SeptBillingGroup || plan.isFree) {
			const enabledPlanFeatureItems = this.billingService.getEnabledPlanFeatureItems(
				plan,
				this.args.previousModel,
			);

			title = enabledPlanFeatureItems.title;
			features = enabledPlanFeatureItems.items.map((item) => {
				let name = item.name;
				if (item.isSoon) {
					name += ' (Soon)';
				}
				return name;
			});
		} else {
			const isLegacy = plan.isLegacy;

			if (plan.isStarter) {
				if (isLegacy) {
					features = [
						'Custom data integrations',
						'Datasets',
						'MCP server',
						'Account templates',
						'Looped dashboards',
					];
				} else {
					features = [
						'AI analyst (Genie)',
						'MCP server',
						'Media creatives',
						'Data stories',
						'Custom metrics',
					];
				}
			} else if (plan.isAnalyst) {
				features = [
					'Custom data integrations',
					'Artifacts',
					'Prebuilt skills',
					'MCP server',
					'API access',
				];
			} else if (plan.isProfessional) {
				features = [
					'Artifacts',
					'Prebuilt skills',
					'Verify & badge assets',
					'Custom date ranges',
					'API access',
				];
			} else if (plan.isGrowth) {
				features = [
					'Routines',
					'Custom skills',
					'Dataset transformations',
					'Agents (Soon)',
					'MCP connectors',
				];
			} else if (plan.isPremium) {
				if (isLegacy) {
					features = [
						'Single sign-on (SSO)',
						'Advanced security',
						'Fiscal calendar',
						'OKRs',
						'Priority support',
					];
				} else {
					features = [
						'White-labeling',
						'Advanced security',
						'Fiscal calendar',
						'OKRs',
						'Priority support',
					];
				}
			}
		}

		return {
			title,
			features: features.map((f) => {
				return { name: f, isEnabled: true };
			}),
		};
	}

	get isExpiredTrial() {
		return this.billingService.isExpiredTrial;
	}

	get isCurrentPlanActionable() {
		return (
			this.isExpiredTrial ||
			this.billingService.isTrialWithoutPayment ||
			this.billingService.isCanceled
		);
	}

	get isSubscribeShown() {
		return this.isExpiredTrial && !this.args.model.isFree;
	}

	get isExpiredBannerShown() {
		return this.isSubscribeShown && this.args.isCurrentPlan;
	}

	// --- Growth plan promo (May 2024)

	get availableDiscountPromotions() {
		const promotions = [];
		if (this.args.model.isGrowth) {
			const growth2024 =
				this.flowPromotionsService.promotions.growthPlanDiscountOptionPromotion;
			if (growth2024) {
				promotions.push(growth2024);
			}
		}

		return promotions;
	}

	get selectedDiscountPromotion() {
		return this.availableDiscountPromotions[0]; // select the first one if any, since there's up to one promotion available currently
	}

	get selectedDiscountPromotionCTAText() {
		const selectedPromotion = this.selectedDiscountPromotion;

		if (selectedPromotion) {
			const timeLeftText = selectedPromotion.timeLeftText;
			return `<b>Claim offer</b> (${timeLeftText} left)`;
		} else {
			return '';
		}
	}

	get selectedDiscountPromotionCycleDescription() {
		const billingCycle = this.args.model.billingCycle;
		return this.selectedDiscountPromotion!.billingDescriptions?.[billingCycle] ?? '';
	}

	get formattedDiscountPrice() {
		let currentPlan = this.args.model,
			currentBillingCycle = this.args.forBillingCycle;

		const activePlan = this.billingService.activePlan,
			availablePlans = this.billingService.availablePlans,
			isYearlyBillingCycle = activePlan?.billingCycle === BillingCycle.YEARLY,
			yearlyPlan = this.billingService.availablePlans.find(
				(plan) =>
					plan.isYearly &&
					plan.planType === currentPlan.planType &&
					plan.isAgency === currentPlan.isAgency,
			)!;

		// show the discount of the current plan compared to the yearly plan
		if (!isYearlyBillingCycle && currentPlan.isYearly) {
			currentBillingCycle = activePlan?.billingCycle || BillingCycle.MONTHLY;
			currentPlan = availablePlans.find(
				(plan) =>
					plan.planType === currentPlan.planType &&
					plan.billingCycle === currentBillingCycle &&
					plan.isAgency === currentPlan.isAgency,
			)!;
		}

		let price;
		const billedSourcesSliderCount = this.billedSourcesSliderCount,
			yearlyPlanTotalPrice = yearlyPlan?.getTotalPriceWithSources(billedSourcesSliderCount),
			currentPlanTotalPrice = currentPlan?.getTotalPriceWithSources(billedSourcesSliderCount);

		if (yearlyPlan && currentPlan && currentBillingCycle === BillingCycle.MONTHLY) {
			price = currentPlanTotalPrice * 12 - yearlyPlanTotalPrice;
		} else if (yearlyPlan && currentPlan && currentBillingCycle === BillingCycle.QUARTERLY) {
			price = currentPlanTotalPrice * 4 - yearlyPlanTotalPrice;
		}

		return price ? getFormattedPrice(price) : null;
	}

	get showPlanAnnualDiscount() {
		return (
			this.billingService.activePlan?.billingCycle !== BillingCycle.YEARLY &&
			!this.args.model.isFree &&
			!this.args.model.isCustom
		);
	}

	get isYearlyIntervalSelected() {
		return this.args.forBillingCycle === BillingCycle.YEARLY;
	}

	// --- Growth plan promo (May 2024)

	// --- 	Trial higher plan

	get activeTrialPromotion() {
		// at most one active trial or offer promotion exists per plan type
		const planType = this.args.model.planType,
			promotions = this.flowPromotionsService.promotions;

		return (
			promotions.getHigherPlanAutomatedTrialActivePromotionForPlanType(planType) ??
			promotions.getHigherPlanAutomatedTrialOfferActivePromotionForPlanType(planType)
		);
	}

	get activeTrialPromotionCTAText() {
		const timeLeftText = this.activeTrialPromotion?.timeLeftText,
			ctaText = 'End trial';

		if (timeLeftText!.includes('few')) {
			return ctaText;
		}

		return timeLeftText ? `${ctaText} (${timeLeftText} left)` : ctaText;
	}

	get availableTrialPromotion(): Promotion | undefined {
		return this.flowPromotionsService.getAvailableTrialPromotion(this.args.model);
	}

	// --- 	Trial higher plan

	showAndHideTooltipOnSlider($el: JQuery<HTMLElement>) {
		const questionEl = $el.parent().find('.question'),
			tooltipEl = $el.find('.tooltip-2');

		tooltipEl.toggleClass(' tooltip-2--not-visible tooltip-2--visible');
		questionEl.toggleClass('question--not-visible question--visible');
	}

	updateSourcesValue($el: JQuery<HTMLElement>, value: number) {
		this.sourcesSliderCount = value;

		$el.find('.tooltip-2 .connections').text(value + ' data source' + (value > 1 ? 's' : ''));
	}

	@action
	initSlider(node: HTMLElement) {
		const $el = $(node).find('.slider');

		const sliderMax = Math.max(
			this.sourcesSliderCount,
			this.accountService.isAgency ? 500 : this.args.model.isPremium ? 300 : 100,
		);

		$el.slider({
			orientation: 'horizontal',
			range: 'min',
			min: this.sourceSliderMinimumCount,
			max: sliderMax,
			step: 1,
			value: this.sourcesSliderCount,
			slide: (_event: Event, ui: SliderUIParams) => {
				this.updateSourcesValue($el, ui.value);
			},
			change: (_event: Event, ui: SliderUIParams) => {
				this.updateSourcesValue($el, ui.value);
			},
			start: () => {
				this.showAndHideTooltipOnSlider($el);
			},
			stop: () => {
				this.showAndHideTooltipOnSlider($el);
			},
			create: () => {
				const sourcesCount = this.sourcesSliderCount,
					tooltip =
						'<div class="tooltip-2 tooltip-2--not-visible"><div class="tooltip-2__inner"><span class="connections">' +
						sourcesCount +
						' data sources</span></div><div class="tooltip-2__arrow"></div></div>';

				$el.find('.ui-slider-handle').html(tooltip);
			},
		});
	}

	@action
	endAutomatedTrial() {
		if (!this.isEndAutomatedTrialInProgress) {
			this.isEndAutomatedTrialInProgress = true;
			void this.flowPromotionsService
				.updatePromotion({ promotionId: this.activeTrialPromotion!.id, commitment: false })
				.finally(() => {
					this.isEndAutomatedTrialInProgress = false;
					this.automatedPopupQueueService.checkToOpenPopup();
				});
		}
	}

	@action
	requestTrial() {
		if (!this.isRequestTrialInProgress) {
			if (this.availableTrialPromotion) {
				this.flowPromotionsService.openStartHigherPlanAutomatedTrialPopup(this.args.model);
			} else {
				void this.billingService.requestTrial(null, this.args.model, 'pricing_page');
			}
		}
	}

	@action
	switchPlan() {
		this.billingService.switchPlan(this.args.model, {
			billingPeriodChangePopup: 'pricing_page_upgrade',
		});
	}

	@action
	editPaymentInfo() {
		if (this.isCurrentPlanActionable) {
			this.billingService.changePayment();
		}
	}

	@action
	onMouseEnter() {
		this.isHoveringOnSlider = true;
	}

	@action
	onMouseLeave(e: MouseEvent) {
		const tooltipElement = document.querySelector('.tooltipster-base');
		if (!tooltipElement?.contains(e.relatedTarget as Node)) {
			this.isHoveringOnSlider = false;
		}
	}

	@action
	contactSales() {
		window.open(this.billingService.customPlanMeetingUrl, '_blank');
	}

	<template>
		<div
			data-id={{@model.id}}
			class={{joinClasses
				(array
					"plan"
					this.planTypeClass
					this.activeClass
					(if this.isUltimateAgencyPlan "agency--ultimate")
					(if this.selectedDiscountPromotion "promotion-discount")
					(if this.activeTrialPromotion "promotion-trialing")
					(if this.isLegacy "legacy")
				)
			}}
		>
			{{#if this.selectedDiscountPromotion}}
				<div class="plan__promotion-banner">
					Promotional offer -{{this.selectedDiscountPromotion.totalDiscountAmmountFormatted}}
					{{#if this.selectedDiscountPromotionCycleDescription}}
						<ToolTipComponent
							@text={{this.selectedDiscountPromotionCycleDescription}}
						/>
					{{/if}}
				</div>
			{{else if this.activeTrialPromotion}}
				<div class="plan__promotion-banner">
					Trial active
				</div>
			{{else if this.isPlanPromoShown}}
				<div class="plan__promotion-banner plan__promotion-banner--recommended">
					Best value for you
					<ToolTipWithIconComponent
						@text="Based on your usage and companies similar to yours."
						@maxWidth={{320}}
					/>
				</div>
			{{else if this.isExpiredBannerShown}}
				<div class="plan__promotion-banner plan__promotion-banner--expired">
					Your trial plan
				</div>
			{{else if this.isLegacy}}
				<div class="plan__promotion-banner plan__promotion-banner--legacy">
					Legacy plan
				</div>
			{{/if}}

			<div class="plan__header">
				<div class="name {{if @model.isAgency 'name--agency'}}">{{@model.name}}</div>
				<div class="price">
					{{#unless @model.isCustom}}
						<div class="amount">
							{{#if this.selectedDiscountPromotion}}
								<div class="compare">
									<span class="currency">$</span>
									<span class="value">
										{{this.planSumPriceMonthlyNoCurrencyFormatted}}
									</span>
								</div>
								<div class="main">
									{{this.planSumPriceMonthlyWithDiscountPromotionFormatted}}
									{{#unless @model.isFree}}
										<span class="cycle">/month</span>
									{{/unless}}
								</div>
							{{else}}
								<div class="main">
									{{this.planSumPriceMonthlyFormatted}}
									{{#unless @model.isFree}}
										<span class="cycle">/month</span>
									{{/unless}}
								</div>
							{{/if}}
						</div>
						<span
							class="billing-cycle-total
								{{if this.showBilledCycleTotal 'billing-cycle-total--shown'}}"
						>
							Billed
							{{@model.billingCycleTextLong}}:
							{{#if this.selectedDiscountPromotion}}
								<span class="cycle-price-wrapper">
									<span class="compare">{{this.planSumPriceFormatted}}</span>
									<span class="main">
										{{this.planSumPriceWithDiscountPromotionFormatted}}
									</span>
								</span>
							{{else}}
								<span class="main">{{this.planSumPriceFormatted}}</span>
							{{/if}}
						</span>
					{{/unless}}
				</div>
			</div>

			<div
				class={{joinClasses
					(array
						"plan__under-header"
						(if this.underHeaderFixedHeight "plan__under-header--fixed-height")
						(if
							this.isSliderHidden
							"plan__under-header--slider-hidden"
							"plan__under-header--slider-shown"
						)
					)
				}}
				{{on "mouseenter" this.onMouseEnter}}
				{{on "mouseleave" this.onMouseLeave}}
			>
				<div class="plan__slider">
					{{#if this.showCurrentPlanInfo}}
						<div class="list">
							<div class="list_row">
								<div class="list_cell">
									<strong>{{@model.name}} plan</strong>
								</div>
								<div class="list_cell">
									<span class="price">
										<strong>{{@model.priceFormatted}}</strong>
										/{{@model.billingCycleTextShort}}
									</span>
								</div>
							</div>
							<div class="list_row list_row--full">
								<span class="info">
									({{this.minimumSourcesCount}}
									data sources incl.)
								</span>
							</div>
							<div class="list_row">
								<div class="list_cell">
									<strong>{{this.freeSources}}</strong>
									free data sources
								</div>
								<div class="list_cell">
									<span class="price">
										<strong>$0.00</strong>
										/{{@model.billingCycleTextShort}}
									</span>
								</div>
							</div>

							{{#if this.isExtraSourcesAllowedOnPlan}}
								<div class="list_row">
									<div class="list_cell">
										<strong>{{this.billedSourcesSliderCount}}</strong>
										add'l data source{{if this.sourcesCount "s"}}
									</div>
									<div class="list_cell">
										<span class="price">
											<strong>
												{{this.planSourcesSumPriceFormatted}}
											</strong>
											/{{@model.billingCycleTextShort}}
										</span>
									</div>
								</div>
								<div class="list_row list_row--full">
									<span class="info">
										({{@model.sourcePriceFormatted}}/{{@model.billingCycleTextShort}}
										per add'l data source)
									</span>
								</div>
							{{/if}}

							{{#if this.appliedDiscount}}
								<div class="list_row">
									<div class="list_cell">
										<strong>Discount</strong>
									</div>
									<div class="list_cell">
										<span class="price">
											<strong>
												{{this.appliedDiscountFormatted}}
											</strong>
											/{{@model.billingCycleTextShort}}
										</span>
									</div>
								</div>
							{{/if}}
						</div>
					{{/if}}

					{{#if this.showPlanDescription}}
						<div class="plan__description">
							{{@model.description}}
						</div>
					{{else}}
						{{#if this.isExtraSourcesAllowedOnPlan}}
							<div class="plan__slider-main" {{didInsert this.initSlider}}>
								<span class="question question--visible">
									How many data sources do you need?
								</span>
								<div class="slider"></div>

								<div class="plan__features plan__features--simple">
									<ul>
										<li>
											<span class="value">{{this.sourcesSliderCount}}</span>
											data sources
											<span class="info">
												{{this.freeSources}}
												included +
												{{@model.sourcePriceFormatted}}/{{@model.billingCycleTextShort}}
												per add'l
											</span>
										</li>
									</ul>
								</div>

							</div>
						{{else}}
							{{#if @model.freeSources}}
								<div class="plan__features plan__features--simple">
									<ul>
										<li>
											<span class="value">{{@model.freeSources}}</span>
											data sources included
										</li>
									</ul>
								</div>
							{{/if}}
						{{/if}}
					{{/if}}
				</div>

			</div>

			<BillingPlanTopFeaturesComponent @model={{@model}} />

			<div class="plan__features plan__features--with-checkboxes">
				{{#unless this.isUltimateAgencyPlan}}
					<div class="plan__features-list-title">
						{{this.additionalFeaturesInfo.title}}
					</div>
					<ul class="plan__features-list">
						{{#each this.additionalFeaturesInfo.features as |feature|}}
							<li class={{if feature.isEnabled "enabled" "disabled"}}>
								<span class="value">
									{{#if feature.isEnabled}}
										<CommonUiIconComponent @name="Check" @size="md" />
									{{else}}
										<CommonUiIconComponent @name="Close" @size="md" />
									{{/if}}
								</span>
								<span>{{{feature.name}}}</span>
								{{#if feature.info}}
									<ToolTipWithIconComponent
										@text={{{feature.info}}}
										@iconName="Question"
										@maxWidth={{230}}
									/>
								{{/if}}
							</li>
						{{/each}}
					</ul>
				{{/unless}}
			</div>

			<div class="plan__actions">

				<div class="annual-discount">
					{{#if this.showPlanAnnualDiscount}}
						{{#if this.isYearlyIntervalSelected}}
							You will save
							<span class="annual-discount__amount">
								{{this.formattedDiscountPrice}}
							</span>
						{{else}}
							Save
							<span class="annual-discount__amount">
								{{this.formattedDiscountPrice}}
							</span>
							with annual billing
						{{/if}}
					{{/if}}
				</div>

				<div class="center">
					{{#if @model.isCustom}}

						<a
							role="button"
							class={{joinClasses
								(array "button" "size-big" "theme-violet-outlined")
							}}
							{{on "click" this.contactSales}}
						>
							Contact sales
						</a>

					{{else if @isCurrentPlan}}
						<a
							role="button"
							class={{joinClasses
								(array
									"button"
									"size-big"
									(if this.isSubscribeShown "upgradable" "activated")
									(if this.billingService.isTrial "trialing")
									(if this.isCurrentPlanActionable "actionable")
									(if
										this.billingService.isTrialWithoutPayment
										"trialing--no-payment"
									)
								)
							}}
							{{on "click" this.editPaymentInfo}}
						>
							{{#if this.billingService.isActiveTrial}}
								<CommonUiIconComponent @name="Clock" @size="sm" />
								{{this.activeTimeLeftOnTrialFormattedText}}
								{{#if this.billingService.isTrialWithoutPayment}}
									<span class="add-payment">Add payment</span>
								{{/if}}
							{{else if this.billingService.isExpiredTrial}}
								<span>Subscribe</span>
							{{else if this.billingService.isCanceled}}
								<CommonUiIconComponent @name="Clock" @size="sm" />
								Reactivate
							{{else}}
								<span>Current plan</span>
							{{/if}}
						</a>
					{{else}}

						{{#if this.showTrialOption}}
							<a
								role="button"
								class="button size-big upgradable"
								{{on "click" this.requestTrial}}
							>
								{{#if this.availableTrialPromotion}}
									{{#if this.hasAnyPlanActiveAutomatedTrial}}
										Change trial
									{{else}}
										Try 14 days for free
									{{/if}}
								{{else if this.billingService.hadTrial}}
									Change trial plan
								{{else}}
									{{#if this.isRequestTrialInProgress}}
										<BarLoaderComponent @theme="white" />
									{{else}}
										Start free 14-day trial
									{{/if}}
								{{/if}}
							</a>
							{{#if this.availableTrialPromotion}}
								<a
									role="button"
									class="empty-button"
									{{on "click" this.switchPlan}}
								>
									<CommonUiIconComponent
										@name="ArrowUpCircleFill"
										@size="sm"
									/>Upgrade now
								</a>
							{{/if}}

						{{else}}

							{{#if this.isDowngrade}}
								<a
									role="button"
									class="button size-big
										{{if this.isSubscribeShown 'upgradable' 'downgradable'}}"
									{{on "click" this.switchPlan}}
								>
									{{#if this.isSamePlanDifferentBillingCycle}}
										Change to
										{{@model.billingCycleTextLong}}
									{{else}}
										{{#if this.isSubscribeShown}}
											Subscribe
										{{else}}
											Downgrade
										{{/if}}
									{{/if}}
								</a>
							{{else}}
								{{#if this.activeTrialPromotion}}
									<a
										role="button"
										class="button size-big upgradable"
										{{on "click" this.endAutomatedTrial}}
									>
										{{#if this.isEndAutomatedTrialInProgress}}
											<BarLoaderComponent @theme="white" />
										{{else}}
											{{{this.activeTrialPromotionCTAText}}}
										{{/if}}
									</a>
									<a
										role="button"
										class="empty-button"
										{{on "click" this.switchPlan}}
									>
										<CommonUiIconComponent
											@name="ArrowUpCircleFill"
											@size="sm"
										/>Upgrade now
									</a>
								{{else}}
									<a
										role="button"
										class="button size-big upgradable"
										{{on "click" this.switchPlan}}
									>
										{{#if this.selectedDiscountPromotionCTAText}}
											{{{this.selectedDiscountPromotionCTAText}}}
										{{else}}
											{{#if this.isExpiredTrial}}
												Subscribe
											{{else}}
												Change plan
											{{/if}}
										{{/if}}
									</a>
								{{/if}}
							{{/if}}

						{{/if}}

					{{/if}}
				</div>

			</div>
		</div>
	</template>
}
